import React, { useState, useEffect, useCallback } from 'react';
import { outreachAPI } from '../services/api';
import useIsMobile from '../hooks/useIsMobile';

const TEMPLATE_TYPES = [
  {
    id: 'COLD_OUTREACH',
    icon: '🚀',
    label: 'Cold Outreach',
    desc: 'First email to a brand you\'ve never worked with',
    color: '#6366f1',
  },
  {
    id: 'FOLLOWUP',
    icon: '🔁',
    label: 'Follow-Up',
    desc: 'Gentle nudge after no reply to your first email',
    color: '#f59e0b',
  },
  {
    id: 'RATE_NEGOTIATION',
    icon: '💼',
    label: 'Rate Negotiation',
    desc: 'Counter-propose when their offer is too low',
    color: '#10b981',
  },
  {
    id: 'CAMPAIGN_BRIEF',
    icon: '📋',
    label: 'Campaign Brief Request',
    desc: 'Ask for all details before creating content',
    color: '#3b82f6',
  },
  {
    id: 'THANK_YOU',
    icon: '🙏',
    label: 'Thank You / Wrap-Up',
    desc: 'Post-campaign wrap-up with performance summary',
    color: '#ec4899',
  },
  {
    id: 'CUSTOM',
    icon: '✏️',
    label: 'Custom Email',
    desc: 'Blank template — write your own message',
    color: '#64748b',
  },
];

const STATUS_COLORS = {
  DRAFT:     { bg: '#f1f5f9', text: '#64748b' },
  SENT:      { bg: '#dbeafe', text: '#1d4ed8' },
  REPLIED:   { bg: '#d1fae5', text: '#065f46' },
  CONVERTED: { bg: '#fef3c7', text: '#92400e' },
  IGNORED:   { bg: '#fee2e2', text: '#991b1b' },
};

const PLATFORMS = ['YouTube', 'Instagram', 'Twitter/X', 'LinkedIn', 'Facebook', 'TikTok', 'Pinterest', 'Blog'];

export default function OutreachPage() {
  const isMobile = useIsMobile();
  const [outreaches, setOutreaches]       = useState([]);
  const [selectedType, setSelectedType]   = useState(null);
  const [form, setForm]                   = useState({
    brandName: '', brandEmail: '', brandWebsite: '', brandInstagram: '',
    yourName: '', niche: '', platform: 'Instagram', followerCount: '', yourRate: '', notes: '',
  });
  const [preview, setPreview]             = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [loading, setLoading]             = useState(false);
  const [copied, setCopied]               = useState(false);
  const [view, setView]                   = useState('compose'); // compose | history
  const [editingId, setEditingId]         = useState(null);
  const [statusLoading, setStatusLoading] = useState(null);
  const [error, setError]                 = useState('');

  const load = useCallback(async () => {
    try {
      const res = await outreachAPI.getAll();
      setOutreaches(res.data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    if (!selectedType) return setError('Please select a template type first.');
    if (!form.brandName.trim()) return setError('Brand name is required.');
    setError('');
    setLoading(true);
    try {
      const payload = { ...form, templateType: selectedType };
      const res = editingId
        ? await outreachAPI.update(editingId, payload)
        : await outreachAPI.generate(payload);
      setPreview(res.data);
      setEditingId(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!preview) return;
    const text = `Subject: ${preview.subject}\n\n${preview.body}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleStatus = async (id, status) => {
    setStatusLoading(id + status);
    try {
      await outreachAPI.updateStatus(id, { status });
      await load();
      if (selectedRecord?.id === id) setSelectedRecord(prev => ({ ...prev, status }));
    } catch {}
    setStatusLoading(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this outreach record?')) return;
    try {
      await outreachAPI.delete(id);
      if (preview?.id === id) setPreview(null);
      if (selectedRecord?.id === id) setSelectedRecord(null);
      await load();
    } catch {}
  };

  const loadIntoForm = (record) => {
    setSelectedType(record.templateType);
    setForm({
      brandName: record.brandName || '',
      brandEmail: record.brandEmail || '',
      brandWebsite: record.brandWebsite || '',
      brandInstagram: record.brandInstagram || '',
      yourName: record.yourName || '',
      niche: record.niche || '',
      platform: record.platform || 'Instagram',
      followerCount: record.followerCount || '',
      yourRate: record.yourRate || '',
      notes: record.notes || '',
    });
    setPreview(record);
    setEditingId(record.id);
    setView('compose');
  };

  const resetCompose = () => {
    setSelectedType(null);
    setForm({ brandName: '', brandEmail: '', brandWebsite: '', brandInstagram: '',
              yourName: '', niche: '', platform: 'Instagram', followerCount: '', yourRate: '', notes: '' });
    setPreview(null);
    setEditingId(null);
    setError('');
  };

  return (
    <div style={{ padding: '2rem', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.8rem', margin: 0, color: 'var(--ink)' }}>
            📧 Brand Outreach
          </h1>
          <p style={{ color: '#64748b', marginTop: '0.3rem', fontSize: '0.9rem' }}>
            Professional email templates to land brand deals — personalised & ready to send
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {['compose', 'history'].map(v => (
            <button key={v} onClick={() => setView(v)}
              style={{ padding: '0.55rem 1.1rem', borderRadius: 8, border: '1px solid #e2e8f0',
                       background: view === v ? 'var(--ink)' : 'white',
                       color: view === v ? 'white' : '#64748b',
                       fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', textTransform: 'capitalize' }}>
              {v === 'compose' ? '✍️ Compose' : `📬 History (${outreaches.length})`}
            </button>
          ))}
        </div>
      </div>

      {view === 'compose' ? (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
          {/* LEFT — Input Panel */}
          <div>
            {/* Template Type Picker */}
            <div style={{ background: 'white', borderRadius: 16, padding: '1.5rem',
                          border: '1px solid #e2e8f0', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#374151', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                1 — Choose Template Type
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                {TEMPLATE_TYPES.map(tt => (
                  <button key={tt.id} onClick={() => setSelectedType(tt.id)}
                    style={{
                      padding: '0.8rem', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                      border: `2px solid ${selectedType === tt.id ? tt.color : '#e2e8f0'}`,
                      background: selectedType === tt.id ? tt.color + '10' : 'white',
                      transition: 'all 0.15s',
                    }}>
                    <div style={{ fontSize: '1.3rem', marginBottom: '0.2rem' }}>{tt.icon}</div>
                    <div style={{ fontWeight: 700, fontSize: '0.8rem', color: selectedType === tt.id ? tt.color : '#1e293b' }}>
                      {tt.label}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.15rem', lineHeight: 1.3 }}>
                      {tt.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Form Fields */}
            <div style={{ background: 'white', borderRadius: 16, padding: '1.5rem',
                          border: '1px solid #e2e8f0', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#374151', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                2 — Fill In Details
              </div>

              <div style={{ fontWeight: 600, fontSize: '0.78rem', color: '#6366f1', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Brand Info
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '1rem' }}>
                {[
                  { key: 'brandName', label: 'Brand Name *', placeholder: 'e.g. Mamaearth' },
                  { key: 'brandEmail', label: 'Brand Email', placeholder: 'collab@brand.com' },
                  { key: 'brandWebsite', label: 'Website', placeholder: 'www.brand.com' },
                  { key: 'brandInstagram', label: 'Brand Instagram', placeholder: '@brandhandle' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>
                      {f.label}
                    </label>
                    <input value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #e2e8f0',
                               fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                ))}
              </div>

              <div style={{ fontWeight: 600, fontSize: '0.78rem', color: '#10b981', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Your Creator Info
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Your Name</label>
                  <input value={form.yourName} onChange={e => setForm(p => ({ ...p, yourName: e.target.value }))}
                    placeholder="e.g. Priya Sharma"
                    style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Platform</label>
                  <select value={form.platform} onChange={e => setForm(p => ({ ...p, platform: e.target.value }))}
                    style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.82rem', outline: 'none', background: 'white', boxSizing: 'border-box' }}>
                    {PLATFORMS.map(pl => <option key={pl} value={pl}>{pl}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Niche</label>
                  <input value={form.niche} onChange={e => setForm(p => ({ ...p, niche: e.target.value }))}
                    placeholder="e.g. Beauty, Tech, Finance"
                    style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Followers / Subscribers</label>
                  <input value={form.followerCount} onChange={e => setForm(p => ({ ...p, followerCount: e.target.value }))}
                    placeholder="e.g. 125K"
                    style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Your Rate</label>
                  <input value={form.yourRate} onChange={e => setForm(p => ({ ...p, yourRate: e.target.value }))}
                    placeholder="e.g. ₹15,000 per reel or ₹8,000 per post"
                    style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Notes (internal)</label>
                  <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                    placeholder="Internal notes about this outreach..."
                    rows={2}
                    style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.82rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                </div>
              </div>
            </div>

            {error && (
              <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.65rem 1rem', fontSize: '0.82rem', color: '#991b1b', marginBottom: '0.8rem' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button onClick={handleGenerate} disabled={loading}
                style={{ flex: 1, padding: '0.75rem', borderRadius: 10, border: 'none',
                         background: loading ? '#94a3b8' : 'var(--ink)', color: 'white',
                         fontWeight: 700, fontSize: '0.88rem', cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Generating…' : editingId ? '🔄 Re-generate Email' : '✨ Generate Email'}
              </button>
              {(selectedType || preview) && (
                <button onClick={resetCompose}
                  style={{ padding: '0.75rem 1.1rem', borderRadius: 10, border: '1px solid #e2e8f0',
                           background: 'white', color: '#64748b', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* RIGHT — Preview Panel */}
          <div>
            {preview ? (
              <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', position: 'sticky', top: '1rem' }}>
                {/* Preview Header */}
                <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0',
                              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>
                      Email Preview
                    </div>
                    <div style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem' }}>
                      {TEMPLATE_TYPES.find(t => t.id === preview.templateType)?.icon} {TEMPLATE_TYPES.find(t => t.id === preview.templateType)?.label}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={handleCopy}
                      style={{ padding: '0.45rem 0.9rem', borderRadius: 8, border: 'none',
                               background: copied ? '#10b981' : 'rgba(255,255,255,0.15)',
                               color: 'white', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>
                      {copied ? '✅ Copied!' : '📋 Copy All'}
                    </button>
                    <button onClick={() => handleDelete(preview.id)}
                      style={{ padding: '0.45rem 0.75rem', borderRadius: 8, border: 'none',
                               background: 'rgba(239,68,68,0.2)', color: '#fca5a5',
                               fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>
                      🗑
                    </button>
                  </div>
                </div>

                {/* Subject */}
                <div style={{ padding: '0.85rem 1.5rem', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subject: </span>
                  <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>{preview.subject}</span>
                </div>

                {/* Body */}
                <div style={{ padding: '1.25rem 1.5rem', maxHeight: 480, overflowY: 'auto' }}>
                  <pre style={{ fontFamily: 'inherit', whiteSpace: 'pre-wrap', fontSize: '0.84rem', color: '#374151', lineHeight: 1.7, margin: 0 }}>
                    {preview.body}
                  </pre>
                </div>

                {/* Status Bar */}
                <div style={{ padding: '0.85rem 1.5rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc',
                              display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', marginRight: '0.3rem' }}>Status:</span>
                  {['DRAFT','SENT','REPLIED','CONVERTED','IGNORED'].map(s => {
                    const col = STATUS_COLORS[s];
                    return (
                      <button key={s} onClick={() => handleStatus(preview.id, s)}
                        disabled={statusLoading !== null}
                        style={{ padding: '0.25rem 0.7rem', borderRadius: 20, border: 'none', cursor: 'pointer',
                                 background: preview.status === s ? col.bg : '#f1f5f9',
                                 color: preview.status === s ? col.text : '#94a3b8',
                                 fontWeight: preview.status === s ? 700 : 500,
                                 fontSize: '0.72rem', transition: 'all 0.15s' }}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={{ background: 'white', borderRadius: 16, border: '2px dashed #e2e8f0',
                            height: 400, display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📧</div>
                <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.4rem', color: '#64748b' }}>
                  Your email preview will appear here
                </div>
                <div style={{ fontSize: '0.82rem', lineHeight: 1.5 }}>
                  Pick a template type → fill in your details → click Generate Email
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* HISTORY VIEW */
        <div>
          {outreaches.length === 0 ? (
            <div style={{ background: 'white', borderRadius: 16, border: '2px dashed #e2e8f0',
                          padding: '4rem 2rem', textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📬</div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#64748b' }}>No outreach emails yet</div>
              <div style={{ fontSize: '0.82rem', marginTop: '0.4rem' }}>
                Go to Compose and generate your first brand outreach email
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '0.8rem' }}>
              {outreaches.map(r => {
                const tt = TEMPLATE_TYPES.find(t => t.id === r.templateType);
                const sc = STATUS_COLORS[r.status] || STATUS_COLORS.DRAFT;
                return (
                  <div key={r.id}
                    style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0',
                             padding: '1.2rem 1.5rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                                  background: (tt?.color || '#64748b') + '18',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>
                      {tt?.icon || '📧'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1e293b' }}>
                          {r.brandName || 'Unknown Brand'}
                        </span>
                        <span style={{ padding: '0.15rem 0.6rem', borderRadius: 20, fontSize: '0.7rem',
                                       fontWeight: 700, background: sc.bg, color: sc.text }}>
                          {r.status}
                        </span>
                        <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                          {tt?.label}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.3rem',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        📌 {r.subject}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                        {r.platform} · {r.followerCount} followers · {new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                      {/* Status quick-update */}
                      {r.status === 'DRAFT' && (
                        <button onClick={() => handleStatus(r.id, 'SENT')}
                          style={{ padding: '0.4rem 0.75rem', borderRadius: 8, border: 'none', cursor: 'pointer',
                                   background: '#dbeafe', color: '#1d4ed8', fontWeight: 600, fontSize: '0.75rem' }}>
                          Mark Sent
                        </button>
                      )}
                      {r.status === 'SENT' && (
                        <button onClick={() => handleStatus(r.id, 'REPLIED')}
                          style={{ padding: '0.4rem 0.75rem', borderRadius: 8, border: 'none', cursor: 'pointer',
                                   background: '#d1fae5', color: '#065f46', fontWeight: 600, fontSize: '0.75rem' }}>
                          Got Reply
                        </button>
                      )}
                      {r.status === 'REPLIED' && (
                        <button onClick={() => handleStatus(r.id, 'CONVERTED')}
                          style={{ padding: '0.4rem 0.75rem', borderRadius: 8, border: 'none', cursor: 'pointer',
                                   background: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '0.75rem' }}>
                          Converted!
                        </button>
                      )}
                      <button onClick={() => loadIntoForm(r)}
                        style={{ padding: '0.4rem 0.75rem', borderRadius: 8, border: '1px solid #e2e8f0',
                                 background: 'white', color: '#374151', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}>
                        ✏️ Edit
                      </button>
                      <button onClick={() => handleDelete(r.id)}
                        style={{ padding: '0.4rem 0.65rem', borderRadius: 8, border: '1px solid #fee2e2',
                                 background: '#fff5f5', color: '#ef4444', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}>
                        🗑
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
